import { useToggle } from "@/hooks/use-toggle";
import { cn } from "@/lib/utils";
import { PropsWithClassName } from "@/types";
import { withStopPropagation } from "@/utils/with-stop-propagation";
import { CopyIcon } from "@radix-ui/react-icons";
import { CircleCheckIcon } from "lucide-react";
import { AnimatePresence, motion, MotionConfig, Variants } from "motion/react";
import React from "react";

const variants: Variants = {
  initial: {
    opacity: 0,
    scale: 0,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
    scale: 0,
  },
};

interface Props {
  readonly onCopy: () => void;
  readonly iconClassName?: string;
}

export function CopyButton(props: PropsWithClassName<Props>) {
  const copiedToggle = useToggle();

  const handleCopy = () => {
    copiedToggle.toggle();
    props.onCopy();
  };

  React.useEffect(() => {
    if (!copiedToggle.value) {
      return;
    }
    const timeout = setTimeout(() => {
      copiedToggle.toggle();
    }, 1000);

    return () => clearTimeout(timeout);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedToggle.value]);

  return (
    <button
      type="button"
      onClick={withStopPropagation(handleCopy)}
      className={cn(
        "grid size-9 place-items-center rounded-md bg-transparent",
        props.className,
      )}
    >
      <MotionConfig transition={{ duration: 0.3 }}>
        <AnimatePresence mode="popLayout">
          {copiedToggle.value ? (
            <motion.div
              key="pasted"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <CircleCheckIcon className={cn("size-4", props.iconClassName)} />
            </motion.div>
          ) : (
            <motion.div
              key="paste"
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <CopyIcon className={cn("size-4", props.iconClassName)} />
            </motion.div>
          )}
        </AnimatePresence>
      </MotionConfig>
    </button>
  );
}
